import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  VideoCameraSlash,
  VideoCamera,
  ArrowUp,
  ChatTeardropDots,
  X,
  CircleNotch,
  Check,
} from "@phosphor-icons/react";
import {
  nextDay,
  getDay,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  formatDistanceToNow,
  isWithinInterval,
  subMinutes,
  addMinutes,
  addWeeks,
  isBefore,
  format,
} from "date-fns";
import { tr } from "date-fns/locale";
import {
  ILiveClassSchedule,
  qoLiveClasses,
  qoLessonRequests,
  ILessonRequest,
} from "../../utils/queryOptions";
import { myAxios } from "../../utils/common";

type Day = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const REQUEST_TYPES = [
  { value: "practice", label: "Practice Çözümü", refLabel: "Pratik ID" },
  { value: "project", label: "Proje Yardımı", refLabel: "Proje Başlığı" },
  { value: "other", label: "Diğer", refLabel: "Konu (opsiyonel)" },
] as const;

type RequestType = (typeof REQUEST_TYPES)[number]["value"];

function getNextClassDate(
  dayOfWeek: number,
  hour: number,
  minute: number,
  durationMinutes: number,
  excludedDates?: string[] | null
): Date {
  const now = new Date();
  const todayDow = getDay(now);

  let classDate: Date;

  if (todayDow === dayOfWeek) {
    classDate = setMilliseconds(
      setSeconds(setMinutes(setHours(now, hour), minute), 0),
      0
    );
    if (isBefore(addMinutes(classDate, durationMinutes), now)) {
      classDate = nextDay(now, dayOfWeek as Day);
      classDate = setMilliseconds(
        setSeconds(setMinutes(setHours(classDate, hour), minute), 0),
        0
      );
    }
  } else {
    classDate = nextDay(now, dayOfWeek as Day);
    classDate = setMilliseconds(
      setSeconds(setMinutes(setHours(classDate, hour), minute), 0),
      0
    );
  }

  if (excludedDates?.length) {
    const excluded = new Set(excludedDates.map((d) => d.slice(0, 10)));
    while (excluded.has(format(classDate, "yyyy-MM-dd"))) {
      classDate = addWeeks(classDate, 1);
    }
  }

  return classDate;
}

// ── Lesson Request Form (embedded) ──

function LessonRequestSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [requestType, setRequestType] = useState<RequestType>("practice");
  const [refLabel, setRefLabel] = useState("");
  const [moduleLabel, setModuleLabel] = useState("");
  const [content, setContent] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const { data: allRequests } = useQuery(qoLessonRequests());
  const requests = allRequests?.filter((r) => r.status === "pending");

  const mutation = useMutation({
    mutationFn: (data: {
      request_type: string;
      content: string;
      cohort_id: number;
      reference_label?: string;
      module_label?: string;
    }) => myAxios.post("/lesson-requests", data),
    onSuccess: () => {
      setContent("");
      setRefLabel("");
      setModuleLabel("");
      setShowSuccess(true);
      queryClient.invalidateQueries({ queryKey: ["lessonRequests"] });
      setTimeout(() => {
        setShowSuccess(false);
        setIsOpen(false);
      }, 2000);
    },
  });

  const handleSubmit = () => {
    if (!content.trim() || !user?.cohort_id) return;
    mutation.mutate({
      request_type: requestType,
      content: content.trim(),
      cohort_id: user.cohort_id,
      reference_label: refLabel.trim() || undefined,
      module_label: moduleLabel.trim() || undefined,
    });
  };

  const currentType = REQUEST_TYPES.find((t) => t.value === requestType)!;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 text-sm text-white/50 hover:text-white/70 transition-colors cursor-pointer mt-1"
      >
        <ChatTeardropDots size={16} weight="fill" className="text-myBlue shrink-0" />
        <span>Canlı derste neler konuşalım?</span>
        {requests?.length ? (
          <span className="text-[10px] bg-myBlue/10 text-myBlue px-1.5 py-0.5 rounded-full font-bold">
            {requests.length}
          </span>
        ) : null}
      </button>

      {/* Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="bg-surface-1 border border-white/[0.08] rounded-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2.5">
                <ChatTeardropDots size={20} weight="fill" className="text-myBlue" />
                <h2 className="text-base font-semibold">Canlı Derste Neler Konuşalım?</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-5 flex flex-col gap-4">
              {/* Type selector */}
              <div className="flex gap-2 flex-wrap">
                {REQUEST_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setRequestType(type.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                      requestType === type.value
                        ? "bg-myBlue text-white"
                        : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70"
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              {/* Inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-white/35 pl-0.5 font-medium">
                    {currentType.refLabel}
                  </label>
                  {requestType === "practice" ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm font-mono">#</span>
                      <input
                        type="number"
                        value={refLabel}
                        onChange={(e) => setRefLabel(e.target.value)}
                        placeholder="6823"
                        className="w-full bg-white/5 border border-white/[0.08] rounded-xl text-white text-sm pl-7 pr-3 py-2.5 focus:outline-none focus:border-myBlue/50 placeholder:text-white/20 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={refLabel}
                      onChange={(e) => setRefLabel(e.target.value)}
                      placeholder={requestType === "project" ? "örn: SQL Basics" : "örn: Array metodları"}
                      className="bg-white/5 border border-white/[0.08] rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-myBlue/50 placeholder:text-white/20 transition-colors"
                    />
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs text-white/35 pl-0.5 font-medium">Modül</label>
                  <input
                    type="text"
                    value={moduleLabel}
                    onChange={(e) => setModuleLabel(e.target.value)}
                    placeholder="örn: Modül 4 – React"
                    className="bg-white/5 border border-white/[0.08] rounded-xl text-white text-sm px-3 py-2.5 focus:outline-none focus:border-myBlue/50 placeholder:text-white/20 transition-colors"
                  />
                </div>
              </div>

              {/* Textarea */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-white/35 pl-0.5 font-medium">Açıklama</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Nerde takıldın? Neler konuşalım?"
                  rows={3}
                  className="w-full bg-white/5 border border-white/[0.08] rounded-xl text-white text-sm px-3 py-2.5 resize-none focus:outline-none focus:border-myBlue/50 placeholder:text-white/20 transition-colors"
                />
              </div>

              {/* Submit */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={!content.trim() || mutation.isPending}
                  className="bg-myBlue hover:bg-myBlue/80 disabled:bg-white/5 disabled:text-white/20 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed"
                >
                  {mutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <CircleNotch size={14} className="animate-spin" /> Gönderiliyor
                    </span>
                  ) : (
                    "Gönder"
                  )}
                </button>
                {showSuccess && (
                  <span className="text-sm text-myGreen flex items-center gap-1.5">
                    <Check size={14} weight="bold" /> Gönderildi
                  </span>
                )}
              </div>

              {/* Previous requests */}
              {requests && requests.length > 0 && (
                <div className="border-t border-white/[0.06] pt-3 space-y-2">
                  <p className="text-xs text-white/30 font-semibold">Önceki Talepler</p>
                  {requests.map((req: ILessonRequest) => (
                    <div key={req.id} className="bg-white/[0.03] rounded-xl px-3 py-2.5 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-semibold bg-myBlue/10 text-myBlue px-1.5 py-0.5 rounded">
                          {REQUEST_TYPES.find((t) => t.value === req.request_type)?.label || req.request_type}
                        </span>
                        {req.module_label && (
                          <span className="text-[11px] text-white/25">{req.module_label}</span>
                        )}
                        <span className="text-[10px] text-white/20 ml-auto">
                          {new Date(req.created_at).toLocaleDateString("tr-TR")}
                        </span>
                      </div>
                      <p className="text-white/50 text-xs">{req.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Single Banner ──

function SingleBanner({
  config,
  now,
  compact = false,
}: {
  config: ILiveClassSchedule;
  now: Date;
  compact?: boolean;
}) {
  const classDate = getNextClassDate(
    config.day_of_week,
    config.hour,
    config.minute,
    config.duration_minutes,
    config.excluded_dates
  );

  const isActive = isWithinInterval(now, {
    start: subMinutes(classDate, config.pre_active_minutes),
    end: addMinutes(classDate, config.duration_minutes),
  });

  const isToday =
    getDay(now) === config.day_of_week &&
    format(classDate, "yyyy-MM-dd") === format(now, "yyyy-MM-dd") &&
    !isBefore(addMinutes(classDate, config.duration_minutes), now);

  const formattedDate = format(classDate, "d MMMM EEEE", { locale: tr });
  const timeStr = format(classDate, "HH:mm");
  const distance = formatDistanceToNow(classDate, {
    locale: tr,
    addSuffix: true,
  });

  if (compact) {
    return (
      <div className="flex items-center gap-2.5 py-1.5">
        <div className="shrink-0 w-7 h-7 rounded-lg bg-myGreen/10 flex items-center justify-center">
          {isActive ? (
            <VideoCamera size={14} weight="fill" className="text-myGreen" />
          ) : (
            <VideoCameraSlash size={14} className="text-white/30" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white/60 truncate">
            {isToday ? (
              <>Bugün {timeStr}</>
            ) : (
              <>{format(classDate, "d MMM EEE", { locale: tr })} {timeStr}</>
            )}
          </p>
        </div>
        {isActive ? (
          <a
            href={config.zoom_link}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs bg-myGreen hover:bg-myGreen/85 text-black px-3 py-1.5 font-bold rounded-lg shrink-0 transition-colors"
          >
            Katıl
          </a>
        ) : (
          isActive === false && (
            <span className="text-[10px] text-white/20 shrink-0">{distance}</span>
          )
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl overflow-hidden border border-white/[0.08] bg-surface-1">
      {/* Class info */}
      <div className="p-4 sm:p-5 flex items-center gap-4">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-myGreen/10 flex items-center justify-center">
          {isActive ? (
            <VideoCamera size={20} weight="fill" className="text-myGreen" />
          ) : (
            <VideoCameraSlash size={20} className="text-white/30" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-myGreen/70 uppercase tracking-wider">
              {config.label}
            </span>
            {isActive && (
              <span className="text-[10px] bg-myGreen/20 text-myGreen px-1.5 py-0.5 rounded-full font-bold animate-pulse">
                CANLI
              </span>
            )}
          </div>
          <p className="text-sm text-white/80 mt-0.5">
            {isToday ? (
              <>
                Bugün <span className="font-semibold">{timeStr}</span>
                {!isActive && (
                  <span className="text-white/40"> &middot; {distance}</span>
                )}
              </>
            ) : (
              <>
                <span className="font-semibold">{formattedDate}</span> saat{" "}
                <span className="font-semibold">{timeStr}</span>
                <span className="text-white/40"> &middot; {distance}</span>
              </>
            )}
          </p>
        </div>

        {isActive ? (
          <a
            href={config.zoom_link}
            target="_blank"
            rel="noreferrer noopener"
            className="shrink-0 bg-myGreen text-black px-4 py-2 font-semibold rounded-xl inline-flex items-center gap-1.5 hover:bg-myGreen/90 transition-colors text-sm"
          >
            Katıl
            <span className="rotate-90">
              <ArrowUp size={14} weight="bold" className="animate-bounce" />
            </span>
          </a>
        ) : (
          <div className="shrink-0 bg-white/5 text-white/30 px-4 py-2 font-medium rounded-xl cursor-default text-sm">
            {timeStr}'da açılacak
          </div>
        )}
      </div>

      {/* Embedded lesson request form */}
      <LessonRequestSection />
    </div>
  );
}

// ── Main Component ──

export default function LiveClassBanner({ compact = false }: { compact?: boolean } = {}) {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());

  const { data: classes } = useQuery(
    qoLiveClasses({ cohort_id: user?.cohort_id ?? 0 })
  );

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(interval);
  }, []);

  if (!user) return null;

  if (compact) {
    return (
      <div className="bg-surface-1 border border-white/[0.06] rounded-xl p-3 space-y-1">
        <p className="text-[10px] text-white/30 font-bold uppercase tracking-wider mb-1">Canlı Dersler</p>
        {classes?.map((c) => (
          <SingleBanner key={c.id} config={c} now={now} compact />
        ))}
        {(!classes || classes.length === 0) && (
          <p className="text-xs text-white/20 py-1">Planlanmış ders yok</p>
        )}
        <div className="pt-1">
          <LessonRequestSection />
        </div>
      </div>
    );
  }

  return (
    <>
      {classes?.map((c) => (
        <SingleBanner key={c.id} config={c} now={now} />
      ))}
    </>
  );
}
